'use client'

import { useEffect } from 'react'
import { InputText, Text } from '@vinted/web-ui'
import { useFormContext } from 'react-hook-form'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useFormValidationMessage } from '@marketplace-web/vendor-abstractions/react-hook-form-feature'
import { BusinessRegistrationModel } from '@marketplace-web/business-accounts/business-accounts-data'

import { NewBusinessAddressField } from '../../../../../../constants'
import { InputDropdownController, InputDropdownOption } from '../InputDropdownController'

const TRANSLATION_PREFIX = 'business.registration'

type Props = {
  cityOptions: Array<InputDropdownOption>
}

const CityInputField = ({ cityOptions }: Props) => {
  const translate = useTranslate(`${TRANSLATION_PREFIX}.addresses`)
  const {
    register,
    setValue,
    formState: { errors },
  } = useFormContext<Partial<BusinessRegistrationModel>>()

  const validationMessage = useFormValidationMessage(errors, TRANSLATION_PREFIX)

  const isMultipleCities = cityOptions.length > 1
  const isSingleCity = cityOptions.length === 1
  const defaultCity = cityOptions[0]?.value || ''

  useEffect(() => {
    if (!isSingleCity) {
      setValue(NewBusinessAddressField.City, '')

      return
    }

    setValue(NewBusinessAddressField.City, defaultCity, { shouldValidate: true })
  }, [defaultCity, isSingleCity, setValue])

  const getCityValidationError = () => {
    const errorMessage = validationMessage(NewBusinessAddressField.City)

    if (!errorMessage) return null

    return <Text as="h4" type="caption" text={errorMessage} theme="warning" />
  }

  const renderHint = (hint: string) => <Text as="h4" text={hint} theme="muted" type="caption" />

  if (isMultipleCities) {
    return (
      <InputDropdownController
        title={translate('business_address.city.title_v2')}
        options={cityOptions}
        input={NewBusinessAddressField.City}
        translationPrefix={TRANSLATION_PREFIX}
        required={translate('business_address.city.multiple.errors.required')}
        placeholder={translate('business_address.city.multiple.placeholder')}
        hint={
          getCityValidationError() || renderHint(translate('business_address.city.multiple.hint'))
        }
        readOnly
        testId="city-input-dropdown"
      />
    )
  }

  return (
    <InputText
      // `required: false` property is needed for react-hook-form to recognize the field as optional when
      // it rerenders from required `InputDropdownController` to optional `InputText`
      {...register(NewBusinessAddressField.City, { required: false })}
      styling="wide"
      title={translate('business_address.city.title_v2')}
      placeholder={translate('business_address.city.single.placeholder')}
      disabled
      validation={
        getCityValidationError() || renderHint(translate('business_address.city.single.hint'))
      }
      testId="city-input-field"
    />
  )
}

export default CityInputField
